import axios from '@/lib/axios'
import { requireOrganizationId, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type { OrganizationIdQueryKey, OrganizationIndexQueryKey } from './organizations.query'
import type {
  CreatedOrganization,
  CreateOrganizationInput,
  MarkOrganizationLiveResult,
  Organization,
  OrganizationLifecycleReadiness,
  OrganizationOnboardingStatus,
  OrganizationRepresentative,
  StorefrontCacheInvalidation,
  UpdateOrganizationInput,
} from './organizations.types'

export async function getOrganizations({
  queryKey: [{ filters = {} }],
}: QueryFunctionContext<OrganizationIndexQueryKey>) {
  return (await axios<Organization[]>('/organizations', { params: filters })) || []
}

export async function getOrganization({
  queryKey: [{ organizationId }],
}: QueryFunctionContext<OrganizationIdQueryKey>) {
  return (
    (await axios<Organization>(`/organizations/${requireOrganizationId(organizationId)}`)) || {}
  )
}

export async function getOrganizationRepresentatives() {
  return (await axios<OrganizationRepresentative[]>('/organizations/representatives')) || []
}

export async function getOrgOnboardingStatus({
  queryKey: [{ organizationId }],
}: QueryFunctionContext<OrganizationIdQueryKey>) {
  return await axios<OrganizationOnboardingStatus>(
    `/organizations/onboarding/${requireOrganizationId(organizationId)}`
  )
}

export async function getOrganizationLifecycleReadiness({
  queryKey: [{ organizationId }],
}: QueryFunctionContext<OrganizationIdQueryKey>) {
  return await axios<OrganizationLifecycleReadiness>(
    `/organizations/${requireOrganizationId(organizationId)}/lifecycle-readiness`
  )
}

export async function createOrganization(values: CreateOrganizationInput) {
  return axios.post<CreatedOrganization>('/organizations', values)
}

export async function updateOrganization(id: OrganizationId, values: UpdateOrganizationInput) {
  return axios.put<Organization>(`/organizations/${id}`, values)
}

export async function markOrganizationLost(id: OrganizationId) {
  return axios.put<Organization>(`/organizations/${id}/lost`)
}

export async function markOrganizationLive(id: OrganizationId) {
  return axios.put<MarkOrganizationLiveResult>(`/organizations/${id}/live`)
}

export async function deleteOrganization(id: OrganizationId) {
  return axios.delete<void>(`/organizations/${id}`)
}

export async function invalidateOrganizationStorefrontCache(id: OrganizationId) {
  return axios.post<StorefrontCacheInvalidation>(`/organizations/${id}/storefront-cache/invalidate`)
}
